import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EquipmentEvidenceDialog, WorkerPickerField } from "@/components/equipment/EquipmentDialogs";
import { EquipmentInventoryTable } from "@/components/equipment/EquipmentInventoryTable";
import { EquipmentRejectionBanner } from "@/components/equipment/EquipmentRejectionBanner";
import { useRole } from "@/contexts/RoleContext";
import {
  createEquipmentAsset,
  EquipmentApiError,
  initiateAssetHandover,
  initiateAssetReturn,
  listEquipmentAssets,
  retireEquipmentAsset,
  updateEquipmentAsset,
} from "@/lib/equipmentApi";
import { CLEANING_STAFF_ROLES, isActiveMembership } from "@/lib/cleaningStaffRoles";
import { supabase } from "@/lib/supabase";
import type { EquipmentAsset } from "@/types/equipment";

export function EquipmentInventoryPanel() {
  const { t, i18n } = useTranslation();
  const { currentOrgId, currentRole } = useRole();
  const canEdit = currentRole === "owner" || currentRole === "coordinator";
  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [workers, setWorkers] = useState<{ value: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [editAsset, setEditAsset] = useState<EquipmentAsset | null>(null);
  const [editName, setEditName] = useState("");
  const [editSerial, setEditSerial] = useState("");
  const [handoverAsset, setHandoverAsset] = useState<EquipmentAsset | null>(null);
  const [workerId, setWorkerId] = useState("");
  const [returnAsset, setReturnAsset] = useState<EquipmentAsset | null>(null);

  const load = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const rows = await listEquipmentAssets(currentOrgId);
      setAssets(rows.filter((item) => item.status !== "retired"));
    } catch (error) {
      console.error("[EquipmentInventoryPanel]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : t("admin.equipment.inventory.fetchError"));
    }
  }, [currentOrgId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!currentOrgId) return;
    void (async () => {
      const { data: members, error } = await supabase
        .from("memberships")
        .select("user_id, role, is_active")
        .eq("org_id", currentOrgId)
        .in("role", [...CLEANING_STAFF_ROLES]);
      if (error) {
        console.error("[EquipmentInventoryPanel] memberships", error);
        return;
      }
      const ids = [...new Set((members ?? []).filter((m) => isActiveMembership(m.is_active)).map((m) => m.user_id))];
      if (ids.length === 0) {
        setWorkers([]);
        return;
      }
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", ids);
      if (profileError) {
        console.error("[EquipmentInventoryPanel] profiles", profileError);
        return;
      }
      setWorkers(
        (profiles ?? []).map((p) => ({
          value: p.id,
          label: p.full_name || p.email || t("admin.equipment.workerFallback"),
        })),
      );
    })();
  }, [currentOrgId, i18n.language, t]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setSaving(true);
      await action();
      toast.success(success);
      await load();
    } catch (error) {
      console.error("[EquipmentInventoryPanel]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : t("admin.equipment.inventory.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (asset: EquipmentAsset) => {
    setEditAsset(asset);
    setEditName(asset.name);
    setEditSerial(asset.serial_number ?? "");
  };

  if (!currentOrgId) return null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>{t("admin.equipment.inventory.title")}</CardTitle>
              <CardDescription>{t("admin.equipment.inventory.description")}</CardDescription>
            </div>
            {canEdit && (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t("admin.equipment.inventory.addTool")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t("admin.equipment.inventory.empty")}</p>
          ) : (
            <EquipmentInventoryTable
              assets={assets}
              canEdit={canEdit}
              saving={saving}
              onEdit={openEdit}
              onHandover={setHandoverAsset}
              onReturn={setReturnAsset}
              onRetire={(asset) => void run(() => retireEquipmentAsset(asset.id), t("admin.equipment.inventory.retired"))}
            />
          )}
        </CardContent>

        <EquipmentEvidenceDialog
          open={addOpen}
          title={t("admin.equipment.inventory.addTitle")}
          description={t("admin.equipment.inventory.addDescription")}
          submitLabel={t("admin.equipment.add")}
          saving={saving}
          onOpenChange={(open) => {
            setAddOpen(open);
            if (!open) {
              setName("");
              setSerial("");
            }
          }}
          hidePhotos
          extra={
            <>
              <div className="space-y-2">
                <Label htmlFor="asset-name">{t("admin.equipment.fields.name")}</Label>
                <Input
                  id="asset-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("admin.equipment.inventory.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset-serial">{t("admin.equipment.fields.serialOptional")}</Label>
                <Input id="asset-serial" value={serial} onChange={(e) => setSerial(e.target.value)} />
              </div>
            </>
          }
          onSubmit={(notes) => {
            if (!name.trim()) {
              toast.error(t("admin.equipment.inventory.nameRequired"));
              return;
            }
            void run(async () => {
              await createEquipmentAsset({
                orgId: currentOrgId,
                name,
                serialNumber: serial,
                notes,
              });
              setAddOpen(false);
              setName("");
              setSerial("");
            }, t("admin.equipment.inventory.added"));
          }}
        />

        <EquipmentEvidenceDialog
          open={Boolean(editAsset)}
          title={t("admin.equipment.inventory.editTitle")}
          description={t("admin.equipment.inventory.editDescription")}
          submitLabel={t("common.save")}
          saving={saving}
          hidePhotos
          initialNotes={editAsset?.notes ?? ""}
          extra={
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-asset-name">{t("admin.equipment.fields.name")}</Label>
                <Input id="edit-asset-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-asset-serial">{t("admin.equipment.fields.serialOptional")}</Label>
                <Input id="edit-asset-serial" value={editSerial} onChange={(e) => setEditSerial(e.target.value)} />
              </div>
            </>
          }
          onOpenChange={(open) => {
            if (!open) setEditAsset(null);
          }}
          onSubmit={(notes) => {
            if (!editAsset) return;
            if (!editName.trim()) {
              toast.error(t("admin.equipment.inventory.nameRequired"));
              return;
            }
            void run(async () => {
              await updateEquipmentAsset({
                id: editAsset.id,
                name: editName,
                serialNumber: editSerial,
                notes,
              });
              setEditAsset(null);
            }, t("admin.equipment.inventory.saved"));
          }}
        />

        <EquipmentEvidenceDialog
          open={Boolean(handoverAsset)}
          title={t("admin.equipment.inventory.handoverTitle")}
          description={t("admin.equipment.inventory.handoverDescription")}
          submitLabel={t("admin.equipment.handover")}
          saving={saving}
          initialNotes={handoverAsset?.notes ?? ""}
          extra={<WorkerPickerField options={workers} value={workerId} onChange={setWorkerId} />}
          onOpenChange={(open) => {
            if (!open) {
              setHandoverAsset(null);
              setWorkerId("");
            }
          }}
          onSubmit={(notes, files) => {
            if (!handoverAsset || !workerId) {
              toast.error(t("admin.equipment.inventory.selectWorker"));
              return;
            }
            void run(async () => {
              await initiateAssetHandover({
                orgId: currentOrgId,
                workerId,
                asset: handoverAsset,
                conditionNotes: notes,
                photos: files,
              });
              setHandoverAsset(null);
              setWorkerId("");
            }, t("admin.equipment.inventory.handoverSent"));
          }}
        />

        <EquipmentEvidenceDialog
          open={Boolean(returnAsset)}
          title={t("admin.equipment.inventory.returnTitle")}
          description={t("admin.equipment.inventory.returnDescription")}
          submitLabel={t("admin.equipment.return")}
          saving={saving}
          initialNotes={returnAsset?.notes ?? ""}
          onOpenChange={(open) => {
            if (!open) setReturnAsset(null);
          }}
          onSubmit={(notes, files) => {
            if (!returnAsset) return;
            void run(async () => {
              await initiateAssetReturn({ asset: returnAsset, conditionNotes: notes, photos: files });
              setReturnAsset(null);
            }, t("admin.equipment.inventory.returnSent"));
          }}
        />
      </Card>
      {canEdit && <EquipmentRejectionBanner orgId={currentOrgId} />}
    </div>
  );
}
